<template>
  <div class="section">
    <div class="container" style="max-width: 640px;">
      <!-- Loading -->
      <div v-if="loading" class="text-center" style="padding: 3rem 0;">
        <div class="loading-spinner" style="margin: 0 auto;"></div>
      </div>

      <!-- Not Found -->
      <div v-else-if="!order" class="empty-state">
        <h3 class="empty-title">ไม่พบคำสั่งซื้อ</h3>
      </div>

      <div v-else>
        <!-- Success Message -->
        <div class="card success-card">
          <div class="card-body text-center">
            <div class="success-icon">
              <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h1 class="success-title">สั่งซื้อสินค้าเรียบร้อยแล้ว!</h1>
            <p class="success-subtitle">
              หมายเลขคำสั่งซื้อ: <strong>{{ order.order_number }}</strong>
            </p>
            
            <div class="bank-info">
              <h3 class="bank-title">รายละเอียดการโอนเงิน</h3>
              <p class="bank-amount">
                กรุณาโอนเงินยอด <strong>{{ formatPrice(order.total_amount) }}</strong> ไปยัง:
              </p>
              <div class="bank-details">
                <div class="bank-header">
                  <img src="/images/banks/LogoSCB.png" alt="SCB" class="bank-logo" />
                  <span class="bank-name-text">ธนาคารไทยพาณิชย์ (SCB)</span>
                </div>
                <div class="bank-account-info">
                  <p>ชื่อบัญชี: <strong>ห้างหุ้นส่วนจำกัด ออร่า พฤกษาธิช</strong></p>
                  <p class="account-row">
                    เลขที่บัญชี:
                    <strong class="account-number">596-459786-4</strong>
                    <button type="button" class="copy-btn" @click="copyAccountNumber">
                      คัดลอก
                    </button>
                  </p>
                </div>
              </div>
              <p class="bank-note">
                * โอนเงินแล้วกรุณาอัปโหลดสลิปเพื่อยืนยันการชำระเงิน
              </p>
            </div>
          </div>
        </div>

        <!-- Upload Slip Form -->
        <div class="card mt-6">
          <div class="card-body">
            <h2 class="section-heading">แจ้งชำระเงิน (อัปโหลดสลิป)</h2>
            
            <Form @submit="submitPayment" :validation-schema="paymentSchema" v-slot="{ errors, isSubmitting }">
              <div class="form-group">
                <label class="form-label">วันที่โอน</label>
                <Field 
                  name="transfer_date"
                  type="date" 
                  class="form-input"
                  :class="{ 'border-red-500': errors.transfer_date }"
                  v-model="transferDate"
                />
                <ErrorMessage name="transfer_date" class="text-red-500 text-sm mt-1" />
              </div>
              
              <div class="form-group">
                <label class="form-label">เวลาที่โอน</label>
                <Field 
                  name="transfer_time"
                  type="time" 
                  class="form-input"
                  :class="{ 'border-red-500': errors.transfer_time }"
                  v-model="transferTime"
                />
                <ErrorMessage name="transfer_time" class="text-red-500 text-sm mt-1" />
              </div>

              <div class="form-group">
                <label class="form-label">รูปสลิป</label>
                <input 
                  type="file" 
                  accept="image/*"
                  @change="handleFileUpload"
                  class="form-input file-input"
                  :class="{ 'border-red-500': fileError }"
                />
                <p v-if="fileError" class="text-red-500 text-sm mt-1">{{ fileError }}</p>
              </div>

              <button 
                type="submit" 
                class="btn btn-primary btn-lg w-full"
                :disabled="isSubmitting"
              >
                <span v-if="isSubmitting" class="loading-spinner" style="width: 16px; height: 16px; margin-right: 8px;"></span>
                {{ isSubmitting ? 'กำลังอัปโหลด...' : 'ยืนยันการชำระเงิน' }}
              </button>
            </Form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as yup from 'yup';
import api from '../api';
import { useNotification } from '../composables/useNotification';

const route = useRoute();
const router = useRouter();
const { showSuccess, showError, handleApiError } = useNotification();

const order = ref(null);
const bankInfo = ref({});
const loading = ref(true);
const slipFile = ref(null);
const fileError = ref('');

// ตั้งค่าวันเวลาปัจจุบันเป็นค่าเริ่มต้น
const now = new Date();
const transferDate = ref(now.toISOString().split('T')[0]); // YYYY-MM-DD
const transferTime = ref(now.toTimeString().slice(0, 5)); // HH:MM

const paymentSchema = yup.object({
  transfer_date: yup.string().required('กรุณาเลือกวันที่โอน'),
  transfer_time: yup.string().required('กรุณาเลือกเวลาที่โอน')
});

onMounted(async () => {
  try {
    const [orderRes, settingsRes] = await Promise.all([
      api.get(`/orders/${route.params.orderNumber}`),
      api.get('/settings')
    ]);
    order.value = orderRes.data.data;
    bankInfo.value = settingsRes.data.data || {};
  } catch (error) {
    showError('ไม่สามารถโหลดข้อมูลคำสั่งซื้อได้');
  } finally {
    loading.value = false;
  }
});

const formatPrice = (price) => {
  return new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(price);
};

const ACCOUNT_NUMBER = '596-459786-4';

const copyAccountNumber = async () => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(ACCOUNT_NUMBER);
    } else {
      const el = document.createElement('textarea');
      el.value = ACCOUNT_NUMBER;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    showSuccess('คัดลอกเลขบัญชีแล้ว');
  } catch (e) {
    showError('คัดลอกไม่สำเร็จ กรุณาคัดลอกด้วยตนเอง');
  }
};

const handleFileUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    if (!file.type.startsWith('image/')) {
      fileError.value = 'กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น';
      slipFile.value = null;
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      fileError.value = 'ขนาดไฟล์ต้องไม่เกิน 5MB';
      slipFile.value = null;
      return;
    }
    fileError.value = '';
    slipFile.value = file;
  }
};

const submitPayment = async (values, { setErrors }) => {
  if (!slipFile.value) {
    fileError.value = 'กรุณาอัปโหลดรูปสลิป';
    return;
  }
  
  const formData = new FormData();
  formData.append('image', slipFile.value);
  formData.append('transfer_date', values.transfer_date);
  formData.append('transfer_time', values.transfer_time);

  try {
    await api.post(`/orders/${route.params.orderNumber}/payment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    showSuccess('แจ้งชำระเงินเรียบร้อยแล้ว');
    router.push(`/tracking/${route.params.orderNumber}`);
  } catch (error) {
    handleApiError(error, setErrors);
  }
};
</script>

<style scoped>
.mt-6 {
  margin-top: 1.5rem;
}

.success-card {
  background: linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%);
}

.success-icon {
  color: #16a34a;
  margin-bottom: 1rem;
}

.success-icon svg {
  margin: 0 auto;
}

.success-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.success-subtitle {
  color: var(--color-text-light);
  margin-bottom: 1.5rem;
}

.bank-info {
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  text-align: left;
}

.bank-title {
  font-weight: 700;
  color: var(--color-primary-dark);
  margin-bottom: 0.75rem;
}

.bank-amount {
  margin-bottom: 1rem;
}

.bank-details {
  background: var(--color-bg);
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid var(--color-border);
}

.bank-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--color-border);
}

.bank-logo {
  width: 48px;
  height: 48px;
  object-fit: contain;
  border-radius: 0.5rem;
}

.bank-name-text {
  font-weight: 600;
  font-size: 1rem;
  color: #4e2a84;
}

.bank-account-info p {
  margin-bottom: 0.5rem;
}

.bank-details p {
  margin-bottom: 0.25rem;
}

.account-number {
  font-size: 1.125rem;
  letter-spacing: 0.1em;
}

.account-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.copy-btn {
  margin-left: auto;
  padding: 0.35rem 0.6rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 0.5rem;
  border: 1px solid var(--color-border);
  background: white;
  color: var(--color-text);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.05s ease;
}

.copy-btn:hover {
  background: var(--color-bg);
}

.copy-btn:active {
  transform: translateY(1px);
}

.bank-note {
  font-size: 0.875rem;
  color: var(--color-primary);
  margin-top: 1rem;
}

.section-heading {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 1.5rem;
}

.file-input {
  padding: 0.5rem;
}

.empty-state {
  text-align: center;
  padding: 4rem 1rem;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
}

.border-red-500 {
  border-color: #ef4444 !important;
}

.text-red-500 {
  color: #ef4444;
}

.text-sm {
  font-size: 0.875rem;
}

.mt-1 {
  margin-top: 0.25rem;
}
</style>
